/**
 * EventCatalog - Canonical analytics event name constants
 *
 * ALL event names used across web and mobile live here.
 * Components should never use raw event name strings — they should call
 * typed helper methods from AnalyticsHelpers which reference these constants.
 *
 * Convention: past-tense verb_noun in snake_case (e.g., 'login_completed')
 */

export const EVENTS = {
  // ==========================================
  // AUTHENTICATION
  // ==========================================
  SIGNUP_COMPLETED: 'signup_completed',
  LOGIN_COMPLETED: 'login_completed',
  LOGOUT_COMPLETED: 'logout_completed',
  PASSWORD_RESET_COMPLETED: 'password_reset_completed',

  // ==========================================
  // ONBOARDING
  // ==========================================
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  EXAM_SELECTED: 'exam_selected',
  ASSESSMENT_STARTED: 'assessment_started',
  ASSESSMENT_COMPLETED: 'assessment_completed',
  ONBOARDING_ASSESSMENT_SKIPPED: 'onboarding_assessment_skipped',
  ONBOARDING_ASSESSMENT_QUIZ_COMPLETED: 'onboarding_assessment_quiz_completed',
  ONBOARDING_ASSESSMENT_FEEDBACK: 'onboarding_assessment_feedback',

  // ==========================================
  // DIAGNOSTIC FLOW (Path A — /welcome → quiz → results)
  // ==========================================
  DIAGNOSTIC_START_CLICKED: 'diagnostic_start_clicked',
  DIAGNOSTIC_SKIPPED: 'diagnostic_skipped',
  DIAGNOSTIC_QUESTION_VIEWED: 'diagnostic_question_viewed',
  DIAGNOSTIC_QUESTION_ANSWERED: 'diagnostic_question_answered',
  DIAGNOSTIC_NAVIGATION: 'diagnostic_navigation',
  DIAGNOSTIC_END_TEST_TAPPED: 'diagnostic_end_test_tapped',
  DIAGNOSTIC_RESULTS_VIEWED: 'diagnostic_results_viewed',
  DIAGNOSTIC_REVIEW_FILTER_TAPPED: 'diagnostic_review_filter_tapped',
  DIAGNOSTIC_TOPIC_TAPPED: 'diagnostic_topic_tapped',
  DIAGNOSTIC_COMMUNITY_MODAL_OPENED: 'diagnostic_community_modal_opened',
  DIAGNOSTIC_START_JOURNEY_CLICKED: 'diagnostic_start_journey_clicked',

  // ==========================================
  // SIGN-IN / SIGNUP MODAL (Path B — / overlay)
  // ==========================================
  SIGNIN_MODAL_SHOWN: 'signin_modal_shown',
  SIGNIN_MODAL_DISMISSED: 'signin_modal_dismissed',
  AUTH_METHOD_SELECTED: 'auth_method_selected',
  SIGNIN_MODAL_SIGNUP_LINK_TAPPED: 'signin_modal_signup_link_tapped',
  SIGNIN_MODAL_DIAGNOSTIC_LINK_TAPPED: 'signin_modal_diagnostic_link_tapped',
  SIGNIN_MODAL_FORGOT_PASSWORD_TAPPED: 'signin_modal_forgot_password_tapped',
  SIGNIN_MODAL_LEARN_MORE_TAPPED: 'signin_modal_learn_more_tapped',

  // ==========================================
  // AUTH FAILURES (dedicated events)
  // ==========================================
  LOGIN_FAILED: 'login_failed',
  SIGNUP_FAILED: 'signup_failed',

  // ==========================================
  // STUDY & QUESTIONS
  // ==========================================
  STUDY_SESSION_STARTED: 'study_session_started',
  STUDY_SESSION_COMPLETED: 'study_session_completed',
  QUESTION_BOOKMARKED: 'question_bookmarked',
  STUDY_QUESTIONS_FILTERED: 'study_questions_filtered',
  STUDY_TOOL_SELECTED: 'study_tool_selected',

  // ==========================================
  // TESTS
  // ==========================================
  TEST_STARTED: 'test_started',
  TEST_COMPLETED: 'test_completed',
  TEST_ABANDONED: 'test_abandoned',
  CUSTOM_TEST_CREATED: 'custom_test_created',

  // ==========================================
  // FLASHCARDS
  // ==========================================
  FLASHCARD_BOOKMARKED: 'flashcard_bookmarked',
  FLASHCARD_SESSION_STARTED: 'flashcard_session_started',
  FLASHCARD_SESSION_ENDED: 'flashcard_session_ended',

  // ==========================================
  // SUBSCRIPTION & PURCHASE
  // ==========================================
  SUBSCRIPTION_VIEWED: 'subscription_viewed',
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  SUBSCRIPTION_CANCEL_CLICKED: 'subscription_cancel_clicked',
  CANCEL_REASON_SUBMITTED: 'cancel_reason_submitted',
  CANCEL_INTENT: 'cancel_intent',
  PASS_STORY_MODAL_SHOWN: 'pass_story_modal_shown',
  PASS_STORY_SUBMITTED: 'pass_story_submitted',
  PASS_STORY_SKIPPED: 'pass_story_skipped',
  FEEDBACK_SUBMITTED: 'feedback_submitted',
  WHY_LEFT_SHOWN: 'why_left_shown',
  WHY_LEFT_SUBMITTED: 'why_left_submitted',
  WHY_LEFT_DISMISSED: 'why_left_dismissed',
  WINBACK_BANNER_SHOWN: 'winback_banner_shown',
  WINBACK_BANNER_CLICKED: 'winback_banner_clicked',
  WINBACK_CHECKOUT_STARTED: 'winback_checkout_started',
  PURCHASE_INTENT_STARTED: 'purchase_intent_started',
  PURCHASE_COMPLETED: 'purchase_completed',
  PROMO_CODE_APPLIED: 'promo_code_applied',
  PROMO_CODE_FAILED: 'promo_code_failed',
  LICENSE_CODE_ACTIVATED: 'license_code_activated',

  // ==========================================
  // ARTICLES & CONTENT
  // ==========================================
  ARTICLE_VIEWED: 'article_viewed',
  ARTICLE_BOOKMARKED: 'article_bookmarked',
  ARTICLE_PROGRESS_UPDATED: 'article_progress_updated',
  ARTICLE_REACTION: 'article_reaction',

  // ==========================================
  // QOTD (Question of the Day)
  // ==========================================
  QOTD_VIEWED: 'qotd_viewed',
  QOTD_ANSWERED: 'qotd_answered',
  QOTD_SKIPPED: 'qotd_skipped',

  // ==========================================
  // FEATURE USAGE
  // ==========================================
  PROFILE_UPDATED: 'profile_updated',
  SEARCH_COMPLETED: 'search_completed',
  CONTENT_SHARED: 'content_shared',
  ACCOUNT_DELETION_ATTEMPTED: 'account_deletion_attempted',

  // ==========================================
  // NAVIGATION & PAGE VIEWS
  // ==========================================
  PAGE_VIEWED: 'page_viewed',
  NAVIGATION: 'navigation',
  DEEP_LINK_HANDLED: 'deep_link_handled',

  // Screen-specific view events
  HOME_VIEWED: 'home_viewed',
  DASHBOARD_VIEWED: 'dashboard_viewed',
  PROFILE_VIEWED: 'profile_viewed',
  HISTORY_VIEWED: 'history_viewed',
  STUDY_TOOLS_VIEWED: 'study_tools_viewed',
  QUESTIONS_VIEWED: 'questions_viewed',
  STUDY_QUESTIONS_MENU_VIEWED: 'study_questions_menu_viewed',
  FLASHCARDS_MENU_VIEWED: 'flashcard_menu_viewed',
  ARTICLES_MENU_VIEWED: 'articles_menu_viewed',
  GLOSSARY_VIEWED: 'glossary_viewed',
  ACRONYMS_VIEWED: 'acronyms_viewed',
  PRACTICE_TEST_MENU_VIEWED: 'practice_test_menu_viewed',
  PRACTICE_TEST_REVIEW_VIEWED: 'practice_test_review_viewed',
  TEST_VIEWED: 'test_viewed',
  ONBOARDING_VIEWED: 'onboarding_viewed',
  PRICING_VIEWED: 'pricing_viewed',
  ASSESSMENT_VIEWED: 'assessment_viewed',
  BOOKMARK_VIEWED: 'bookmark_viewed',
  ACCOUNT_SETTINGS_VIEWED: 'account_settings_viewed',
  SUBSCRIPTION_SETTINGS_VIEWED: 'subscription_settings_viewed',
  HELP_SUPPORT_VIEWED: 'help_support_viewed',

  // ==========================================
  // SESSION & LIFECYCLE
  // ==========================================
  SESSION_STARTED: 'session_started',
  SESSION_PAUSED: 'session_paused',
  APP_OPENED: 'app_opened',

  // ==========================================
  // USER ACTIONS
  // ==========================================
  SHARE_APP: 'share_app',
  RESET_APP_DATA: 'reset_app_data',
  APP_THEME_CHANGED: 'app_theme_changed',
  OFFER_CLAIMED: 'offer_claimed',

  // ==========================================
  // REVIEWS & FEEDBACK
  // ==========================================
  REVIEW_APP: 'review_app',
  REVIEW_FROM_HOME: 'review_from_home',
  LOVE_IT_REVIEW: 'love_it_review',
  NEED_WORK_REVIEW: 'need_work_review',
  NOT_REVIEW_YET: 'not_review_yet',

  // ==========================================
  // NOTIFICATIONS
  // ==========================================
  NOTIFICATION_PERMISSION_GRANTED: 'notification_permission_granted',
  NOTIFICATION_PERMISSION_DENIED: 'notification_permission_denied',

  // ==========================================
  // EXAM COUNTDOWN
  // ==========================================
  EXAM_COUNTDOWN_SET_CLICKED: 'exam_countdown_set_clicked',
  EXAM_COUNTDOWN_CHANGE_CLICKED: 'exam_countdown_change_clicked',
  EXAM_COUNTDOWN_DATE_SELECTED: 'exam_countdown_date_selected',
  EXAM_COUNTDOWN_UPDATED: 'exam_countdown_updated',

  // ==========================================
  // DASHBOARD INTERACTIONS (Fix-My-Weakness loop)
  // ==========================================
  DASHBOARD_TILE_CLICKED: 'dashboard_tile_clicked',
  DASHBOARD_DOMAIN_CARD_CLICKED: 'dashboard_domain_card_clicked',
  WEAKEST_DOMAIN_DRILL_STARTED: 'weakest_domain_drill_started',
  PRACTICE_SESSION_MODAL_OPENED: 'practice_session_modal_opened',

  // ==========================================
  // READINESS & PROGRESS
  // ==========================================
  // Note: values emitted before 2026-05-21 are systematically under-counted —
  // multi-select questions were scored as incorrect due to a reference-equality
  // bug in addQuestionScore (fixed in PR #1 / commit on branch
  // fix/multi-select-scoring). Filter on event timestamp when doing historical
  // analysis of this event.
  READINESS_SCORE_UPDATED: 'readiness_score_updated',

  // ==========================================
  // ERRORS & PERFORMANCE
  // ==========================================
  ERROR_OCCURRED: 'error_occurred',
  PERFORMANCE_MEASURED: 'performance_measured',
  PAGE_LOADED: 'page_loaded',
  ENGAGEMENT_RECORDED: 'engagement_recorded',

  // ==========================================
  // MOBILE-SPECIFIC (Phase 5 union — additive; web simply never fires these)
  // Native auth/welcome nav, mobile diagnostic flow, and cost-optimized
  // aggregated menu-click counters.
  // ==========================================
  WELCOME_SCREEN_VIEWED: 'welcome_screen_viewed',
  WELCOME_SIGNIN_TAPPED: 'welcome_signin_tapped',
  LOGIN_SCREEN_VIEWED: 'login_screen_viewed',
  SIGNUP_SCREEN_VIEWED: 'signup_screen_viewed',
  SIGNUP_SIGNIN_LINK_TAPPED: 'signup_signin_link_tapped',
  DIAGNOSTIC_SELECTOR_VIEWED: 'diagnostic_selector_viewed',
  DIAGNOSTIC_POSTPONED: 'diagnostic_postponed',
  DIAGNOSTIC_BACK_TAPPED: 'diagnostic_back_tapped',
  DIAGNOSTIC_TIMER_EXPIRED: 'diagnostic_timer_expired',
  DIAGNOSTIC_START_PREP_TAPPED: 'diagnostic_start_prep_tapped',
  ONBOARDING_ASSESSMENT_QUIZ_ABANDONED: 'onboarding_assessment_quiz_abandoned',
  ONBOARDING_CONGRATULATIONS_VIEWED: 'onboarding_congratulations_viewed',
  STUDY_QUESTIONS_BOOKMARKS_CLICKED: 'study_questions_bookmarks_clicked',
  STUDY_QUESTIONS_QUICKSET_CLICKED: 'study_questions_quickset_clicked',
  STUDY_QUESTIONS_BYTOPIC_CLICKED: 'study_questions_bytopic_clicked',
  FLASHCARDS_BOOKMARKS_CLICKED: 'flashcards_bookmarks_clicked',
  FLASHCARDS_QUICKSET_CLICKED: 'flashcards_quickset_clicked',
  FLASHCARDS_EXAM_ESSENTIALS_CLICKED: 'flashcards_exam_essentials_clicked',
  FLASHCARDS_BYTOPIC_CLICKED: 'flashcards_bytopic_clicked',
  ARTICLES_BOOKMARKS_CLICKED: 'articles_bookmarks_clicked',
  ARTICLES_QUICKSET_CLICKED: 'articles_quickset_clicked',
  ARTICLES_BYTOPIC_CLICKED: 'articles_bytopic_clicked',
  APPEARANCE_SETTINGS_VIEWED: 'appearance_settings_viewed',
  OTHER_APP_VIEWED: 'other_app_viewed',
};

export default EVENTS;
